import Vue from 'vue';
import VueI18n, { LocaleMessages } from 'vue-i18n';
import Locale from '@/utils/locale';

Vue.use(VueI18n);

function loadLocaleMessages(): LocaleMessages {
  return {
    en: require('@/locales/en.json'),
    nb: require('@/locales/nb.json'),
  };
}

export default new VueI18n({
  locale: Locale.get() || process.env.VUE_APP_I18N_LOCALE || 'en',
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
  messages: loadLocaleMessages(),
  numberFormats: {
    'en': {
      currency: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
    'nb': {
      currency: ({
        numberFormatLocale: "fr-FR",
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      } as unknown as Intl.NumberFormatOptions),
    },
  },
  // when using vue-component-translations we get warnings like:[vue-i18n] Value
  // of key 'Address.address' is not a string!. Setting this to true avoids
  // that.
  silentFallbackWarn: true,
  pluralizationRules: {
    'en': (index, choices) => (index >= choices) ? choices - 1 : index,
    'nb': (index, choices) => (index >= choices) ? choices - 1 : index,
  },
});

// After upgrading to chrome v92, and canary v94 Intl.NumberFormat("no") stopped
// working, vue-i18n is using it behind the scenes.
// When you set locate to Norwegian and try to format a number you will get the
// number formatted in English which is the fallback.
// To test it, run:
//  (new Intl.NumberFormat("no-NO")).format("123123")

// It is reported as a bug 26th of june 2021, here are 2 links to issue tracker
// https://bugs.chromium.org/p/chromium/issues/detail?id=1233047&q=intl.&can=2
// https://bugs.chromium.org/p/chromium/issues/detail?id=1232968

// the code below is copied from https://github.com/kazupon/vue-i18n/blob/14c502cff3720a33f8ab4bd88d27e3c6a5d92a03/src/index.js#L937-L982
// with one modification, include numberFormatLocale and the forth last line.
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
function isNull(val: any): boolean {
  return val === null || val === undefined;
}

export function warn(msg: string, err?: Error): void {
  if (typeof console !== 'undefined') {
    console.warn('[vue-i18n] ' + msg);
    /* istanbul ignore if */
    if (err) {
      console.warn(err.stack);
    }
  }
}

(VueI18n.prototype as any)._getNumberFormatter = function _getNumberFormatter(
  value: any,
  locale: any,
  fallback: any,
  numberFormats: any,
  key: any,
  options?: any,
) {
  let _locale = locale;
  let formats = numberFormats[_locale];

  // fallback locale
  if (isNull(formats) || isNull(formats[key])) {
    if (process.env.NODE_ENV !== 'production' && (!this as any)._isSilentTranslationWarn(key) && !(this as any)._isSilentFallbackWarn(key)) {
      warn(("Fall back to '" + fallback + "' number formats from '" + locale + "' number formats."));
    }
    _locale = fallback;
    formats = numberFormats[_locale];
  }

  if (isNull(formats) || isNull(formats[key])) {
    return null;
  } else {
    const format = formats[key];

    let formatter;
    if (options) {
      // If options specified - create one time number formatter
      formatter = new Intl.NumberFormat(_locale, Object.assign({}, format, options));
    } else {
      const id = _locale + "__" + key;
      formatter = (this as any)._numberFormatters[id];
      if (!formatter) {
        formatter = (this as any)._numberFormatters[id] = new Intl.NumberFormat(format.numberFormatLocale || _locale, format);
      }
    }
    return formatter;
  }
};
